// src/components/HeartsRiseOverlay.tsx
// Fullscreen fixed overlay — mounts once in App.tsx, renders nothing until
// a 'crushhh:crushConfirmed' CustomEvent is dispatched. On each event a burst
// of N heart SVGs rises from the bottom edge, fades, then removes itself.
// pointer-events: none throughout — never blocks interaction.

import { useEffect, useRef, useState } from "react";
import "./HeartsRiseOverlay.css";

// ── Feel values — tune these freely ──────────────────────────────────────────
const HEART_COUNT    = 35;    // hearts per burst
const RISE_MS        = 1500; // base travel duration (ms)
const RISE_MS_JITTER = 1500;  // random ± per heart so they don't move in lockstep
const DELAY_MS_MAX   = 450;  // max random stagger before each heart starts (ms)
const SIZE_MIN_PX    = 32;   // smallest heart diameter (px)
const SIZE_MAX_PX    = 106;   // 3× min — wide small-to-large spread per burst
const DRIFT_PX       = 130;   // max horizontal wander during rise (px, ±)
const HEART_COLORS   = ["#E05A3A", "#FF8FA3", "#E84E6B", "#FFB3C1"]; // coral + pink family
// ─────────────────────────────────────────────────────────────────────────────

interface HeartParticle {
  id: number;
  left: string;
  size: number;
  duration: number;
  delay: number;
  drift: number;
  color: string;
}

function randomBetween(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

// Coin-flip then cube: each heart lands near SIZE_MIN or SIZE_MAX, rarely mid-range.
function bimodalSize(): number {
  const r = Math.random() < 0.5
    ? Math.pow(Math.random(), 3)          // cubed toward 0 → small
    : 1 - Math.pow(Math.random(), 3);     // cubed toward 1 → large
  return SIZE_MIN_PX + r * (SIZE_MAX_PX - SIZE_MIN_PX);
}

export default function HeartsRiseOverlay() {
  const [hearts, setHearts] = useState<HeartParticle[]>([]);
  // Incrementing counter — never resets, so overlapping bursts get unique ids.
  const idCounterRef = useRef(0);

  useEffect(() => {
    function handleCrushConfirmed() {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      const burst: HeartParticle[] = Array.from({ length: HEART_COUNT }, () => {
        idCounterRef.current += 1;
        return {
          id:       idCounterRef.current,
          left:     `${8 + Math.random() * 84}%`, // 8–92% keeps hearts off the side edges
          size:     Math.round(bimodalSize()),
          duration: Math.round(RISE_MS + randomBetween(-RISE_MS_JITTER / 2, RISE_MS_JITTER / 2)),
          delay:    Math.round(Math.random() * DELAY_MS_MAX),
          drift:    Math.round(randomBetween(-DRIFT_PX, DRIFT_PX)),
          color:    HEART_COLORS[Math.floor(Math.random() * HEART_COLORS.length)],
        };
      });

      setHearts((prev) => [...prev, ...burst]);
    }

    window.addEventListener("crushhh:crushConfirmed", handleCrushConfirmed);
    return () => {
      window.removeEventListener("crushhh:crushConfirmed", handleCrushConfirmed);
    };
  }, []);

  if (hearts.length === 0) return null;

  return (
    <div
      style={{
        position:      "fixed",
        inset:         0,
        pointerEvents: "none",
        zIndex:        40, // deliberate: UNDER modals at z-50 and fullscreen overlays at z-1000
        overflow:      "hidden",
      }}
    >
      {hearts.map((h) => (
        <span
          key={h.id}
          className="heart-rise-particle"
          style={
            {
              left:              h.left,
              animationDuration: `${h.duration}ms`,
              animationDelay:    `${h.delay}ms`,
              "--drift":         `${h.drift}px`,
            } as React.CSSProperties
          }
          onAnimationEnd={() =>
            setHearts((prev) => prev.filter((p) => p.id !== h.id))
          }
        >
          <svg
            viewBox="0 0 24 24"
            width={h.size}
            height={h.size}
            fill={h.color}
            aria-hidden="true"
          >
            <path d="M12 21.593c-5.63-5.539-11-10.297-11-14.402 0-3.791 3.068-5.191 5.281-5.191 1.312 0 4.151.501 5.719 4.457 1.59-3.968 4.464-4.447 5.726-4.447 2.54 0 5.274 1.621 5.274 5.181 0 4.069-5.136 8.625-11 14.402z" />
          </svg>
        </span>
      ))}
    </div>
  );
}
